import { columnLetter } from "./a1";

// Deciding how much of a sheet to read, BEFORE reading any of it.
//
// The order matters and is the whole reason this is a separate module. Pulling a
// 50,000-row used range through the Excel API is slow in itself -- several seconds of
// round trips and a large array in memory -- so the extractor asks for the used
// range's BOUNDS first, brings them here, and only then loads data. Loading
// speculatively and trimming afterwards would pay the cost we are trying to avoid.
//
// The shape of the answer follows from what a spreadsheet is. A document must be sent
// whole because its meaning is distributed through the prose; a workbook's meaning
// lives in its structure -- headers, formulas, formats, table definitions -- and the
// data rows are instances of that structure. Row 40,000 of an invoice table tells you
// almost nothing that row 40 did not. So structure is always complete and never
// truncated here; only the data rows are budgeted.

/** Under this many cells, the sheet's values are sent whole. Expect to tune this. */
export const FULL_VALUE_CELL_LIMIT = 5_000;

/**
 * Above this, reading every formula in the used range costs more than it is worth as
 * a fallback. Formulas come back as a dense array of mostly empty strings, so the cost
 * is the round trip and the allocation, not the formula count.
 *
 * Only consulted when the host cannot do the scan sparsely -- see
 * `canScanFormulasSparsely` on the input.
 */
export const FORMULA_SCAN_CELL_LIMIT = 200_000;

/** Rows taken from the top of a sampled sheet: enough to see headers and real data. */
export const HEAD_ROWS = 30;

/** Rows taken from the bottom: totals and footers live here. */
export const TAIL_ROWS = 5;

export interface SheetBounds {
  sheetName: string;
  /** Used-range origin, 0-based. Often not A1 -- sheets start wherever they start. */
  firstRow: number;
  firstColumn: number;
  rowCount: number;
  columnCount: number;
  /**
   * True when the host can return just the formula-bearing cells rather than the whole
   * rectangle (Excel's getSpecialCells). Gated at runtime by the caller, because the
   * requirement set it belongs to is documented inconsistently and our manifest floor
   * is ExcelApi 1.5.
   */
  canScanFormulasSparsely: boolean;
}

export interface RowRange {
  /** 0-based offset from the used range's first row. */
  start: number;
  count: number;
}

export interface ExtractionPlan {
  /** Whether every row's values are read, or only the sampled ones. */
  values: "all" | "sampled";
  /** Which rows to read. One entry covering everything when values === "all". */
  rowRanges: RowRange[];
  /**
   * Whether to look for formulas across the whole sheet. When false, formula patterns
   * are derived from the sampled rows only and exceptions elsewhere go unseen -- which
   * the elision says out loud.
   */
  scanFormulas: boolean;
  /**
   * What the model is not being shown, in a sentence, or null when it is being shown
   * everything. This is what makes truncation safe rather than misleading: an assistant
   * told "rows 31-49995 were omitted, use read_range" behaves completely differently
   * from one silently handed a fragment.
   */
  elision: string | null;
}

/** Cells in the used range. */
const cellCount = (bounds: SheetBounds) => bounds.rowCount * bounds.columnCount;

export function planExtraction(bounds: SheetBounds): ExtractionPlan {
  const { rowCount, columnCount, sheetName } = bounds;

  if (rowCount === 0 || columnCount === 0) {
    return { values: "all", rowRanges: [], scanFormulas: false, elision: null };
  }

  const total = cellCount(bounds);
  const scanFormulas = bounds.canScanFormulasSparsely || total <= FORMULA_SCAN_CELL_LIMIT;

  if (total <= FULL_VALUE_CELL_LIMIT) {
    return {
      values: "all",
      rowRanges: [{ start: 0, count: rowCount }],
      scanFormulas,
      elision: null,
    };
  }

  // Sampled. Head and tail, and nothing in between -- with the caveat that the
  // extractor adds back any row holding a formula that deviates from its column's
  // pattern, because those are where the interesting mistakes are.
  const head = Math.min(HEAD_ROWS, rowCount);
  const tail = Math.min(TAIL_ROWS, rowCount - head);
  const rowRanges: RowRange[] = [{ start: 0, count: head }];
  if (tail > 0) {
    rowRanges.push({ start: rowCount - tail, count: tail });
  }

  const omittedFrom = bounds.firstRow + head + 1;
  const omittedTo = bounds.firstRow + rowCount - tail;
  const lastColumn = columnLetter(bounds.firstColumn + columnCount - 1);
  const firstColumn = columnLetter(bounds.firstColumn);

  const omittedCount = omittedTo - omittedFrom + 1;
  const parts = [
    `Sheet "${sheetName}" has ${rowCount.toLocaleString()} rows of data in ` +
      `${firstColumn}:${lastColumn}; only the first ${head} and last ${tail} are included here.`,
    `Rows ${omittedFrom.toLocaleString()}-${omittedTo.toLocaleString()} ` +
      `(${omittedCount.toLocaleString()} rows) were omitted.`,
  ];

  if (scanFormulas) {
    parts.push(
      "Formula patterns below cover the WHOLE sheet, including the omitted rows, " +
        "so the sheet's logic is complete even where its data is not."
    );
  } else {
    parts.push(
      "This sheet was too large to scan for formulas, so the patterns below come " +
        "from the included rows only and there may be formula exceptions elsewhere."
    );
  }

  parts.push("Use read_range to see any omitted rows before reasoning about them.");

  return { values: "sampled", rowRanges, scanFormulas, elision: parts.join(" ") };
}

/** Total rows a plan will actually read -- useful for logging and for tests. */
export function plannedRowCount(plan: ExtractionPlan): number {
  return plan.rowRanges.reduce((total, range) => total + range.count, 0);
}
